import { CalendarEvent, CalendarEventMetrics, NativeAppleEvent } from '@demind-inc/core';
import { useAuthContext, useCalendarContext, useCircadianContext } from '../context';
import {
  useCreateAllDayCalendarEvent,
  useCreateCalendarEvent,
  useDeleteCalendarEvent,
  useSyncAppleCalendarsList,
  useUpdateCalendarEvent,
} from '../mutations';
import { useTodoTasksCache } from '../queries';
import dayjs from 'dayjs';
import {
  CalendarEventCreateOption,
  CalendarEventModifyOption,
  FirestoreTaskItem,
  MovedTaskParam,
} from '../types';
import { useCallback, useEffect, useRef } from 'react';
import { trackEventMixpanel } from '../../utils';
import { useRecoilState, useRecoilValue } from 'recoil';
import { eventsSnackBarAtom, syncedDatesState } from '../recoil';
import { AxiosError } from 'axios';
import { useHandleCalSyncError } from '../../hooks/useHandleCalSyncError';
import { CalendarError401Res } from '../api';
import {
  getFormattedDueDateForUpdateTaskFromEvent,
  removeScheduledTaskTimeFromTask,
} from '../../helpers';
import { hasReadOnlyCalendarScope, transformAllDayEventTime } from '../helpers';
import { useTaskActions } from './useTaskActions';
import {
  deleteRecurringEventFromAppleCalendarDesktop,
  extractAppleOccurrenceDate,
  extractNativeAppleEventId,
  fetchAppleCalendarsFromDesktop,
  getAppleCalendarSyncWindow,
  isAppleCalendarDesktopSupported,
  saveEventToAppleCalendarDesktop,
  transformEventForAppleCalendarDesktop,
  updateEventInAppleCalendarDesktop,
  updateSingleOccurrenceInAppleCalendarDesktop,
} from '../../helpers/appleCalendarDesktop.helpers';
import { translateI18n } from '../../i18n';

export interface CreateCalendarEventParams {
  calendarId?: string;
  newEventOption: CalendarEventCreateOption;
  taskId?: string;
  metrics?: CalendarEventMetrics;
  skipTaskUpdate?: boolean;
  movedTaskParam?: MovedTaskParam;
  suppressEventSnackbar?: boolean;
}

type RecurringActionScope = 'thisEvent' | 'allEvents';

interface DeleteCalendarEventParams {
  event?: Partial<CalendarEvent>;
  calendarId: string;
  unSyncToCalendar?: boolean;
  recurringDeleteSpan?: RecurringActionScope;
}

interface UpdateCalendarEventParams {
  newEventOption: CalendarEventModifyOption;
  eventId: string;
  calendarId?: string;
  skipTaskUpdate?: boolean;
  recurringUpdateSpan?: RecurringActionScope;
  originalOccurrenceDate?: string;
  existingEvent?: Partial<CalendarEvent>;
  suppressEventSnackbar?: boolean;
}

const isRecurringEvent = (event?: Partial<CalendarEvent>) => {
  if (!event) {
    return false;
  }

  if (Object.prototype.hasOwnProperty.call(event, 'recurrence')) {
    return !!(event as Partial<CalendarEvent> & { recurrence?: unknown }).recurrence;
  }

  return !!event.rawJson && !!extractAppleOccurrenceDate(event);
};

const getRecurrenceString = (event?: Partial<CalendarEvent>): string | null => {
  if (!event) {
    return null;
  }

  const recurrence = (event as Partial<CalendarEvent> & { recurrence?: unknown }).recurrence;
  if (!recurrence) {
    return null;
  }

  if (typeof recurrence === 'string') {
    return recurrence;
  }

  if (
    typeof recurrence === 'object' &&
    recurrence !== null &&
    'string' in recurrence &&
    typeof (recurrence as { string?: unknown }).string === 'string'
  ) {
    return (recurrence as { string: string }).string;
  }

  return null;
};

const APPLE_WEB_UNSUPPORTED_MESSAGE_KEY = 'calendar.snackbar.appleWebUnsupported';
const READ_ONLY_CALENDAR_MESSAGE =
  'This calendar is read-only. Choose a writable calendar to make changes.';

export const useEventActions = () => {
  const { user } = useAuthContext();
  const { mainCalendar, findCalendarItem } = useCalendarContext();
  const [, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);
  const syncedDates = useRecoilValue(syncedDatesState);

  const {
    createCalendarEvent: createCalendarEventMutation,
    status: eventCreatingStatus,
    error: eventCreatingError,
  } = useCreateCalendarEvent();
  const { createAllDayCalendarEvent, isCreating: isCreatingAllDayEvent } =
    useCreateAllDayCalendarEvent();
  const { updateCalendarEvent: updateCalendarEventMutation, status: eventUpdatingStatus } =
    useUpdateCalendarEvent();
  const { deleteCalendarEvent: deleteCalendarEventMutation, status: eventDeletingStatus } =
    useDeleteCalendarEvent();
  const { syncAppleCalendarsList } = useSyncAppleCalendarsList();
  const { findPhaseForTaskTime } = useCircadianContext();
  const { updateTodoTask: handleUpdateTask } = useTaskActions();

  const { findInCacheByDate } = useTodoTasksCache();
  const { handleConnect, syncErrorInfo } = useHandleCalSyncError({
    syncErrorRootEmail: ((eventCreatingError as AxiosError)?.response?.data as CalendarError401Res)
      ?.rootEmail,
  });
  const previousEventCreatingStatusRef = useRef(eventCreatingStatus);
  const previousEventUpdatingStatusRef = useRef(eventUpdatingStatus);
  const suppressCreateSnackbarCountRef = useRef(0);
  const suppressUpdateSnackbarCountRef = useRef(0);

  useEffect(() => {
    if (previousEventCreatingStatusRef.current === eventCreatingStatus) {
      return;
    }
    previousEventCreatingStatusRef.current = eventCreatingStatus;

    const isSuppressed = suppressCreateSnackbarCountRef.current > 0;
    if (isSuppressed) {
      if (eventCreatingStatus === 'success' || eventCreatingStatus === 'error') {
        suppressCreateSnackbarCountRef.current = Math.max(
          suppressCreateSnackbarCountRef.current - 1,
          0
        );
      }
      return;
    }

    if (eventCreatingStatus === 'success') {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.eventCreated'), type: 'success' });
    } else if (eventCreatingStatus === 'pending') {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.eventCreating'), type: 'loading' });
    } else if (eventCreatingStatus === 'error') {
      const is401Error = (eventCreatingError as AxiosError)?.response?.status === 401;
      if (is401Error && syncErrorInfo?.calendarType) {
        setEventsSnackbar({
          message: translateI18n('calendar.snackbar.createInvalidToken'),
          actionLabel: translateI18n('calendar.sidebar.reconnect'),
          autoHideDuration: 5000,
          type: 'error',
          onPress: () => {
            handleConnect(syncErrorInfo.calendarType!);
          },
        });
      } else {
        setEventsSnackbar({ message: translateI18n('calendar.snackbar.createFailed'), type: 'error' });
      }
    }
  }, [eventCreatingStatus, eventCreatingError, handleConnect, setEventsSnackbar, syncErrorInfo]);

  useEffect(() => {
    if (previousEventUpdatingStatusRef.current === eventUpdatingStatus) {
      return;
    }
    previousEventUpdatingStatusRef.current = eventUpdatingStatus;

    const isSuppressed = suppressUpdateSnackbarCountRef.current > 0;
    if (isSuppressed) {
      if (eventUpdatingStatus === 'success' || eventUpdatingStatus === 'error') {
        suppressUpdateSnackbarCountRef.current = Math.max(
          suppressUpdateSnackbarCountRef.current - 1,
          0
        );
      }
      return;
    }

    if (eventUpdatingStatus === 'success') {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.eventUpdated'), type: 'success' });
    } else if (eventUpdatingStatus === 'pending') {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.eventUpdating'), type: 'loading' });
    } else if (eventUpdatingStatus === 'error') {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.updateFailed'), type: 'error' });
    }
  }, [eventUpdatingStatus, setEventsSnackbar]);

  const findTargetTaskFromCache = useCallback(
    (startDate: string, boardId: string, taskId: string) => {
      const tasksInCache = findInCacheByDate(dayjs(startDate).format('YYYY-MM-DD'), boardId);
      const taskInCache = tasksInCache.find((t) => t.taskId === taskId);

      return taskInCache;
    },
    [findInCacheByDate]
  );

  const syncAppleCalendarReconciliation = useCallback(
    async (shouldSyncDeletedList = true) => {
      if (!isAppleCalendarDesktopSupported()) {
        return;
      }

      const { startTime, endTime } = getAppleCalendarSyncWindow(syncedDates);
      const appleCalendars = await fetchAppleCalendarsFromDesktop({ startTime, endTime });

      await syncAppleCalendarsList({
        userId: user.userId,
        email: user.email,
        metricId: user.metricId,
        userPersona: user.persona?.job,
        startTime,
        endTime,
        shouldSyncDeletedList,
        appleCalendars,
      });
    },
    [syncedDates, syncAppleCalendarsList, user]
  );

  const upsertLinkedTaskFromEvent = useCallback(
    async (
      newEventOption: Partial<Pick<CalendarEvent, 'summary' | 'start' | 'end' | 'taskFrom'>>,
      options: { movedTaskParam?: MovedTaskParam; skipTaskUpdate?: boolean }
    ) => {
      if (!newEventOption.taskFrom || options.skipTaskUpdate) {
        return;
      }

      const circadianPhase = findPhaseForTaskTime(
        newEventOption.start?.date,
        newEventOption.end?.date
      );

      const newTaskInfo = {
        name: newEventOption.summary,
        ...(newEventOption.start && newEventOption.end
          ? getFormattedDueDateForUpdateTaskFromEvent(
              newEventOption as Pick<CalendarEvent, 'start' | 'end'>
            )
          : {}),
        addedToCalendar: true,
        addedZonePhase: circadianPhase,
        appFrom: newEventOption.taskFrom.from,
      } as Partial<FirestoreTaskItem> & { appFrom: FirestoreTaskItem['appFrom'] };

      const taskInCache = findTargetTaskFromCache(
        newEventOption.start.date,
        newEventOption.taskFrom.boardId,
        newEventOption.taskFrom.taskId
      );

      newTaskInfo.originalTaskId = taskInCache?.originalTaskId;

      await handleUpdateTask({
        taskId: newEventOption.taskFrom.taskId,
        boardId: newEventOption.taskFrom.boardId,
        newTaskInfo,
        movedTaskParam: options.movedTaskParam,
      });
    },
    [findPhaseForTaskTime, findTargetTaskFromCache, handleUpdateTask]
  );

  const createBackendEvent = useCallback(
    async (params: {
      calendarId: string;
      taskId?: string;
      metrics?: CalendarEventMetrics;
      newEventOption: CalendarEventCreateOption;
    }) => {
      if (params.newEventOption.isAllDay) {
        await createAllDayCalendarEvent({
          userId: user.userId,
          calendarId: params.calendarId,
          newEventOption: {
            ...params.newEventOption,
            ...transformAllDayEventTime({
              start: params.newEventOption.start,
              end: params.newEventOption.end,
            }),
          },
        });
        return;
      }

      await createCalendarEventMutation({
        userId: user.userId,
        calendarId: params.calendarId,
        taskId: params.taskId,
        metrics: params.metrics,
        newEventOption: params.newEventOption,
      });
    },
    [createAllDayCalendarEvent, createCalendarEventMutation, user.userId]
  );

  const createCalendarEvent = useCallback(
    async (params: CreateCalendarEventParams) => {
      if (params.suppressEventSnackbar) {
        suppressCreateSnackbarCountRef.current += 1;
      }

      const targetCalendarId = params.calendarId || mainCalendar.calendarId;
      const targetCalendar = findCalendarItem(targetCalendarId);
      const isAppleCalendar = targetCalendar?.calendarType === 'apple';

      if (hasReadOnlyCalendarScope(targetCalendar)) {
        const error = new Error(READ_ONLY_CALENDAR_MESSAGE);
        setEventsSnackbar({ message: error.message, type: 'error' });
        throw error;
      }

      if (isAppleCalendar && !isAppleCalendarDesktopSupported()) {
        const error = new Error(translateI18n(APPLE_WEB_UNSUPPORTED_MESSAGE_KEY));
        setEventsSnackbar({ message: error.message, type: 'error' });
        throw error;
      }

      let nativeAppleEvent: NativeAppleEvent | null = null;
      let targetEventId = params.newEventOption.eventId;

      try {
        if (isAppleCalendar) {
          nativeAppleEvent = await saveEventToAppleCalendarDesktop(
            transformEventForAppleCalendarDesktop(params.newEventOption),
            targetCalendar?.internalGCalendarId
          );

          if (!nativeAppleEvent?.id) {
            throw new Error(translateI18n('calendar.snackbar.appleCreateFailed'));
          }

          targetEventId = nativeAppleEvent.id;
        }

        const backendEventOption: CalendarEventCreateOption = {
          ...params.newEventOption,
          ...(targetEventId ? { eventId: targetEventId } : {}),
          ...(nativeAppleEvent ? { rawJson: JSON.stringify(nativeAppleEvent) } : {}),
        };

        await createBackendEvent({
          calendarId: targetCalendarId,
          taskId: params.taskId,
          metrics: params.metrics,
          newEventOption: backendEventOption,
        });

        await upsertLinkedTaskFromEvent(backendEventOption, {
          movedTaskParam: params.movedTaskParam,
          skipTaskUpdate: params.skipTaskUpdate,
        });

        const shouldSyncRecurringAppleCreate =
          isAppleCalendar && !!getRecurrenceString(params.newEventOption);
        if (shouldSyncRecurringAppleCreate) {
          await syncAppleCalendarReconciliation(false);
        }

        trackEventMixpanel('create_calendar_event', {
          event: params.newEventOption.summary,
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : translateI18n('calendar.snackbar.createFailed');
        if (!params.suppressEventSnackbar) {
          setEventsSnackbar({ message, type: 'error' });
        }
        throw error;
      }
    },
    [
      createBackendEvent,
      findCalendarItem,
      mainCalendar,
      setEventsSnackbar,
      syncAppleCalendarReconciliation,
      upsertLinkedTaskFromEvent,
    ]
  );

  const updateCalendarEvent = useCallback(
    async ({
      newEventOption,
      eventId,
      calendarId,
      skipTaskUpdate,
      recurringUpdateSpan,
      originalOccurrenceDate,
      existingEvent,
      suppressEventSnackbar,
    }: UpdateCalendarEventParams) => {
      if (suppressEventSnackbar) {
        suppressUpdateSnackbarCountRef.current += 1;
      }

      const targetCalendarId = calendarId || mainCalendar.calendarId;
      const targetCalendar = findCalendarItem(targetCalendarId);
      const isAppleCalendar = targetCalendar?.calendarType === 'apple';

      if (hasReadOnlyCalendarScope(targetCalendar)) {
        const error = new Error(READ_ONLY_CALENDAR_MESSAGE);
        setEventsSnackbar({ message: error.message, type: 'error' });
        throw error;
      }

      if (isAppleCalendar && !isAppleCalendarDesktopSupported()) {
        const error = new Error(translateI18n(APPLE_WEB_UNSUPPORTED_MESSAGE_KEY));
        setEventsSnackbar({ message: error.message, type: 'error' });
        throw error;
      }

      const sourceEvent: Partial<CalendarEvent> = existingEvent || {
        eventId,
        calendarId: targetCalendarId,
      };
      let finalNewEventOption = newEventOption;

      try {
        if (isAppleCalendar) {
          const nativeEventId = extractNativeAppleEventId(sourceEvent) || eventId;
          const recurrenceExplicitlySet = Object.prototype.hasOwnProperty.call(
            finalNewEventOption,
            'recurrence'
          );
          const occurrenceDate =
            originalOccurrenceDate ||
            extractAppleOccurrenceDate(sourceEvent) ||
            finalNewEventOption.start?.date;

          if (isRecurringEvent(sourceEvent) && recurringUpdateSpan === 'thisEvent') {
            if (!occurrenceDate) {
              throw new Error(translateI18n('calendar.snackbar.missingOccurrenceUpdate'));
            }

            const updateResult = await updateSingleOccurrenceInAppleCalendarDesktop(
              nativeEventId,
              transformEventForAppleCalendarDesktop(finalNewEventOption),
              dayjs(occurrenceDate).toISOString(),
              targetCalendar?.internalGCalendarId
            );

            if (!updateResult.success || !updateResult.appleEvent?.id) {
              throw new Error(
                translateI18n('calendar.snackbar.appleRecurringOccurrenceUpdateFailed')
              );
            }

            const replacementOption: CalendarEventCreateOption = {
              ...finalNewEventOption,
              eventId: updateResult.appleEvent.id,
              rawJson: JSON.stringify(updateResult.appleEvent),
            };

            await createBackendEvent({
              calendarId: targetCalendarId,
              newEventOption: replacementOption,
            });

            await deleteCalendarEventMutation({
              userId: user.userId,
              eventId,
              calendarId: targetCalendarId,
              unSyncToCalendar: true,
              recurringDeleteSpan: 'thisEvent',
            });

            await upsertLinkedTaskFromEvent(replacementOption, { skipTaskUpdate });
            if (!suppressEventSnackbar) {
              setEventsSnackbar({ message: translateI18n('calendar.snackbar.eventUpdated'), type: 'success' });
            }
            trackEventMixpanel('update_calendar_event', {
              title: finalNewEventOption.summary,
              recurringUpdateSpan: 'thisEvent',
            });
            return;
          }

          if (isRecurringEvent(sourceEvent) && recurringUpdateSpan === 'allEvents') {
            const updateResult = await updateEventInAppleCalendarDesktop(
              nativeEventId,
              transformEventForAppleCalendarDesktop(finalNewEventOption),
              {
                calendarIdentifier: targetCalendar?.internalGCalendarId,
                originalOccurrenceDate: occurrenceDate
                  ? dayjs(occurrenceDate).toISOString()
                  : undefined,
                futureEvents: true,
                recurrenceExplicitlySet,
              }
            );

            if (!updateResult.success) {
              throw new Error(translateI18n('calendar.snackbar.appleRecurringSeriesUpdateFailed'));
            }

            await syncAppleCalendarReconciliation(true);
            if (!suppressEventSnackbar) {
              setEventsSnackbar({ message: translateI18n('calendar.snackbar.eventUpdated'), type: 'success' });
            }
            trackEventMixpanel('update_calendar_event', {
              title: finalNewEventOption.summary,
              recurringUpdateSpan: 'allEvents',
            });
            return;
          }

          const nativeUpdateResult = await updateEventInAppleCalendarDesktop(
            nativeEventId,
            transformEventForAppleCalendarDesktop(finalNewEventOption),
            {
              calendarIdentifier: targetCalendar?.internalGCalendarId,
              recurrenceExplicitlySet,
            }
          );

          if (!nativeUpdateResult.success || !nativeUpdateResult.appleEvent) {
            throw new Error(translateI18n('calendar.snackbar.appleUpdateFailed'));
          }

          finalNewEventOption = {
            ...finalNewEventOption,
            rawJson: JSON.stringify(nativeUpdateResult.appleEvent),
          };
        }

        await updateCalendarEventMutation({
          userId: user.userId,
          calendarId: targetCalendarId,
          eventId,
          newEventOption: finalNewEventOption,
          recurringUpdateSpan,
        });

        await upsertLinkedTaskFromEvent(finalNewEventOption, { skipTaskUpdate });

        trackEventMixpanel('update_calendar_event', { title: finalNewEventOption.summary });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : translateI18n('calendar.snackbar.updateFailed');
        if (!suppressEventSnackbar) {
          setEventsSnackbar({ message, type: 'error' });
        }
        throw error;
      }
    },
    [
      createBackendEvent,
      deleteCalendarEventMutation,
      findCalendarItem,
      mainCalendar,
      setEventsSnackbar,
      syncAppleCalendarReconciliation,
      updateCalendarEventMutation,
      upsertLinkedTaskFromEvent,
      user.userId,
    ]
  );

  const deleteCalendarEvent = useCallback(
    async ({
      event,
      calendarId,
      unSyncToCalendar,
      recurringDeleteSpan,
    }: DeleteCalendarEventParams) => {
      if (!event?.eventId || !calendarId) {
        return;
      }

      const targetCalendar = findCalendarItem(calendarId);
      const isAppleCalendar = targetCalendar?.calendarType === 'apple';

      if (hasReadOnlyCalendarScope(targetCalendar)) {
        const error = new Error(READ_ONLY_CALENDAR_MESSAGE);
        setEventsSnackbar({ message: error.message, type: 'error' });
        throw error;
      }

      if (isAppleCalendar && !isAppleCalendarDesktopSupported()) {
        const error = new Error(translateI18n(APPLE_WEB_UNSUPPORTED_MESSAGE_KEY));
        setEventsSnackbar({ message: error.message, type: 'error' });
        throw error;
      }

      try {
        if (isAppleCalendar) {
          const nativeEventId = extractNativeAppleEventId(event) || event.eventId;
          const deleteSpan = recurringDeleteSpan || 'allEvents';
          const rawOccurrenceDate = extractAppleOccurrenceDate(event) || event.start?.date;
          if (deleteSpan === 'thisEvent' && !rawOccurrenceDate) {
            throw new Error(translateI18n('calendar.snackbar.missingOccurrenceDelete'));
          }
          const occurrenceDate =
            deleteSpan === 'thisEvent' && rawOccurrenceDate
              ? dayjs(rawOccurrenceDate).toISOString()
              : undefined;

          const isDeleted = await deleteRecurringEventFromAppleCalendarDesktop(
            nativeEventId,
            deleteSpan,
            occurrenceDate
          );

          if (!isDeleted) {
            throw new Error(translateI18n('calendar.snackbar.appleDeleteFailed'));
          }

          await deleteCalendarEventMutation({
            userId: user.userId,
            eventId: event.eventId,
            calendarId: calendarId || mainCalendar.calendarId,
            unSyncToCalendar: true,
            recurringDeleteSpan,
          });

          if (deleteSpan === 'allEvents' && isRecurringEvent(event)) {
            await syncAppleCalendarReconciliation(true);
          }
        } else {
          await deleteCalendarEventMutation({
            userId: user.userId,
            eventId: event.eventId,
            calendarId: calendarId || mainCalendar.calendarId,
            unSyncToCalendar,
            recurringDeleteSpan,
          });
        }

        if (event.taskFrom) {
          const taskInCache = findTargetTaskFromCache(
            event.start.date,
            event.taskFrom.boardId,
            event.taskFrom.taskId
          );

          await handleUpdateTask({
            taskId: event.taskFrom.taskId,
            boardId: event.taskFrom.boardId,
            newTaskInfo: {
              ...removeScheduledTaskTimeFromTask(dayjs(event.start?.date).format('YYYY-MM-DD')),
              ...(taskInCache?.name || event.summary
                ? { name: taskInCache?.name || event.summary }
                : {}),
              appFrom: event.taskFrom.from,
              originalTaskId: taskInCache?.originalTaskId,
              addedToCalendar: false,
            },
          });
        }

        trackEventMixpanel('delete_calendar_event');
      } catch (error) {
        const message =
          error instanceof Error ? error.message : translateI18n('calendar.snackbar.deleteFailed');
        setEventsSnackbar({ message, type: 'error' });
        throw error;
      }
    },
    [
      deleteCalendarEventMutation,
      findCalendarItem,
      findTargetTaskFromCache,
      handleUpdateTask,
      mainCalendar,
      setEventsSnackbar,
      syncAppleCalendarReconciliation,
      user.userId,
    ]
  );

  return {
    createCalendarEvent,
    eventCreatingStatus,
    updateCalendarEvent,
    eventUpdatingStatus,
    deleteCalendarEvent,
    eventDeletingStatus,
    createAllDayCalendarEvent,
    isCreatingAllDayEvent,
  };
};
